/**
 * CompanyAssetsPage — iter-237.6 (Feb 2026)
 *
 * Unified page for CloudGM FZ LLC's asset ledger. Uses `?mode=entry|records`:
 *   • entry   → shows only the "Add asset" forms (3 tabs: metals/stocks/real estate)
 *   • records → shows only the read-only ledger + totals
 *   • no mode → shows both (default)
 *
 * Back button routes to the correct parent hub based on mode.
 *
 * Backend routes (see routes/admin_company_assets.py):
 *   GET|POST|PATCH|DELETE /api/admin/company-assets/metals[/:id]
 *   GET|POST|PATCH|DELETE /api/admin/company-assets/stocks[/:id]
 *   GET|POST|PATCH|DELETE /api/admin/company-assets/realestate[/:id]
 *   GET                    /api/admin/company-assets/summary
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Gem, TrendingUp, Home, Plus, Trash2, Loader2, RefreshCcw, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { DirhamPrice } from '../../components/DirhamPrice';
import { floorAED } from '../../utils/money';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/company-assets`;

const TABS = [
  { id: 'metals',     icon: Gem,        color: '#d4af37', labelAr: 'المعادن والمجوهرات', labelEn: 'Metals & Jewelry' },
  { id: 'stocks',     icon: TrendingUp, color: '#3B82F6', labelAr: 'الأسهم',            labelEn: 'Stocks' },
  { id: 'realestate', icon: Home,       color: '#10B981', labelAr: 'العقارات',          labelEn: 'Real Estate' },
];

// ─────────────────────────────────────────────────────────────
// Empty form defaults per type
// ─────────────────────────────────────────────────────────────
const EMPTY_METAL = {
  metal_type: 'gold', name: '', weight_grams: '', karat: '',
  purchase_price_aed: '', paid_amount_aed: '', purchase_date: '',
  seller: '', location: '', notes: '',
};
const EMPTY_STOCK = {
  stock_symbol: '', stock_name: '', stock_type: 'common', market: '',
  quantity: '', unit_price_aed: '', purchase_price_aed: '', paid_amount_aed: '',
  purchase_date: '', broker: '', notes: '',
};
const EMPTY_REALESTATE = {
  property_type: 'apartment', name: '', address: '', emirate: '',
  area_sqm: '', bedrooms: '', bathrooms: '',
  purchase_price_aed: '', paid_amount_aed: '', purchase_date: '',
  seller: '', title_deed_number: '', notes: '',
};

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function CompanyAssetsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const [searchParams] = useSearchParams();
  const mode = (searchParams.get('mode') || '').toLowerCase();
  const isEntryMode   = mode === 'entry';
  const isRecordsMode = mode === 'records';
  const parentPath = isEntryMode
    ? '/finance/asb606/payments/entry'
    : isRecordsMode
      ? '/finance/asb606/payments/records'
      : '/finance/asb606/payments';
  const parentLabelAr = isEntryMode ? 'الإدراج المالي للشركة'
                     : isRecordsMode ? 'السجل والمتابعة'
                     : 'المدفوعات المالية للشركة';
  const parentLabelEn = isEntryMode ? 'Financial Entry'
                     : isRecordsMode ? 'Records & Tracking'
                     : 'Company Financials';

  const [tab, setTab] = useState('metals');
  const [rows, setRows] = useState({ metals: [], stocks: [], realestate: [] });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_METAL);

  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  // ────────────── Data fetch ──────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rMetals, rStocks, rRE, rSum] = await Promise.all([
        axios.get(`${API}/metals`,     { headers }),
        axios.get(`${API}/stocks`,     { headers }),
        axios.get(`${API}/realestate`, { headers }),
        axios.get(`${API}/summary`,    { headers }),
      ]);
      setRows({
        metals:     rMetals.data?.data || [],
        stocks:     rStocks.data?.data || [],
        realestate: rRE.data?.data     || [],
      });
      setSummary(rSum.data || null);
    } catch (e) {
      toast.error(t('فشل تحميل بيانات الأصول', 'Failed to load assets'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Reset the form when the tab changes.
  useEffect(() => {
    if (tab === 'metals')     setForm({ ...EMPTY_METAL });
    else if (tab === 'stocks') setForm({ ...EMPTY_STOCK });
    else                       setForm({ ...EMPTY_REALESTATE });
  }, [tab]);

  // ────────────── Save ──────────────
  const save = async () => {
    // Basic validation
    if (!form.purchase_price_aed || Number(form.purchase_price_aed) <= 0) {
      toast.error(t('سعر الشراء مطلوب وأكبر من صفر', 'Purchase price is required and must be > 0'));
      return;
    }
    // Type-specific validation
    if (tab === 'stocks' && !form.stock_symbol.trim()) {
      toast.error(t('رمز السهم مطلوب', 'Stock symbol is required'));
      return;
    }
    setSaving(true);
    try {
      const url = `${API}/${tab}`;
      // Cast numeric fields
      const payload = { ...form };
      for (const k of ['purchase_price_aed','paid_amount_aed','unit_price_aed','quantity','weight_grams','area_sqm','bedrooms','bathrooms']) {
        if (payload[k] !== undefined && payload[k] !== '') payload[k] = Number(payload[k]);
        else if (payload[k] === '') delete payload[k];
      }
      await axios.post(url, payload, { headers });
      toast.success(t('تمت الإضافة بنجاح', 'Added successfully'));
      // Reset form
      if (tab === 'metals')     setForm({ ...EMPTY_METAL });
      else if (tab === 'stocks') setForm({ ...EMPTY_STOCK });
      else                       setForm({ ...EMPTY_REALESTATE });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  // ────────────── Delete ──────────────
  const deleteRow = async (id) => {
    if (!window.confirm(t('هل أنت متأكد من الحذف؟', 'Delete this asset?'))) return;
    try {
      await axios.delete(`${API}/${tab}/${id}`, { headers });
      toast.success(t('تم الحذف', 'Deleted'));
      fetchAll();
    } catch (e) {
      toast.error(t('فشل الحذف', 'Delete failed'));
    }
  };

  // ────────────── Render ──────────────
  const active = TABS.find((x) => x.id === tab);
  const activeRows = rows[tab] || [];
  const s = summary?.[tab] || { count: 0, total_aed: 0, paid_aed: 0 };

  return (
    <DashboardLayout
      title="أصول الشركة"
      titleEn="Company Assets"
      subtitle={isEntryMode ? 'إدراج أصول جديدة (معادن / أسهم / عقارات)' : 'المعادن، الأسهم، والعقارات'}
      subtitleEn={isEntryMode ? 'Add new assets (metals / stocks / real estate)' : 'Metals, stocks, and real estate'}
      screenCode="ASB-FIN-PAY-ASSET"
      backPath={parentPath}
      onBack={() => navigate(parentPath)}
      breadcrumb={[
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'ASB606 (المالية)', labelEn: 'ASB606 (Finance)', path: '/finance/asb606' },
        { labelAr: parentLabelAr, labelEn: parentLabelEn, path: parentPath },
        { labelAr: 'أصول الشركة', labelEn: 'Assets' },
      ]}
    >
      <section
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
        data-testid="company-assets-page"
      >
        {/* Grand totals — visible in records + default mode only */}
        {!isEntryMode && summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <TotalCard color="#d4af37" labelAr="المعادن" labelEn="Metals"       count={summary.metals?.count}     value={summary.metals?.total_aed}     testId="assets-total-metals" />
            <TotalCard color="#3B82F6" labelAr="الأسهم"  labelEn="Stocks"       count={summary.stocks?.count}     value={summary.stocks?.total_aed}     testId="assets-total-stocks" />
            <TotalCard color="#10B981" labelAr="العقارات" labelEn="Real Estate" count={summary.realestate?.count} value={summary.realestate?.total_aed} testId="assets-total-realestate" />
            <TotalCard color="#c9a227" labelAr="الإجمالي" labelEn="Grand Total" count={summary.grand?.count}      value={summary.grand?.total_aed}      testId="assets-total-grand" />
          </div>
        )}

        {/* Tabs */}
        <div className="inline-flex bg-black/40 border border-white/10 rounded-xl p-1 mb-5" data-testid="assets-type-switch">
          {TABS.map((tt) => {
            const Icon = tt.icon;
            const isActive = tt.id === tab;
            return (
              <button
                key={tt.id}
                type="button"
                onClick={() => setTab(tt.id)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition inline-flex items-center gap-2 ${
                  isActive ? 'text-black' : 'text-white/60 hover:text-white'
                }`}
                style={isActive ? { background: tt.color } : undefined}
                data-testid={`assets-tab-${tt.id}`}
              >
                <Icon className="h-4 w-4" />
                {isAr ? tt.labelAr : tt.labelEn}
              </button>
            );
          })}
        </div>

        {/* Entry form — hidden in records-only mode */}
        {!isRecordsMode && (
          <div
            className="bg-white/[0.02] border rounded-2xl p-5 mb-6"
            style={{ borderColor: `${active.color}40` }}
            data-testid={`assets-form-${tab}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <Plus className="h-5 w-5" style={{ color: active.color }} />
              <h3 className="font-semibold text-white">
                {t(`إضافة ${active.labelAr}`, `Add ${active.labelEn}`)}
              </h3>
            </div>
            {tab === 'metals'     && <MetalsForm     form={form} setForm={setForm} t={t} />}
            {tab === 'stocks'     && <StocksForm     form={form} setForm={setForm} t={t} />}
            {tab === 'realestate' && <RealEstateForm form={form} setForm={setForm} t={t} />}
            <div className="flex justify-end mt-4">
              <Button
                onClick={save}
                disabled={saving}
                className="px-6 h-11 rounded-xl text-white"
                style={{ background: active.color }}
                data-testid={`assets-save-${tab}`}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus className="h-4 w-4 me-2" />{t('إضافة', 'Add')}</>)}
              </Button>
            </div>
          </div>
        )}

        {/* Records list — hidden in entry-only mode */}
        {!isEntryMode && (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5" data-testid={`assets-list-${tab}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white inline-flex items-center gap-2">
                <active.icon className="h-5 w-5" style={{ color: active.color }} />
                {t(`سجل ${active.labelAr}`, `${active.labelEn} Records`)}
                <span className="text-xs font-normal text-white/50 ms-2">
                  ({activeRows.length}) · <DirhamPrice value={floorAED(s.total_aed)} decimals={2} className="text-white/70" />
                </span>
              </h3>
              <Button variant="outline" size="sm" onClick={fetchAll} className="border-white/10 text-white/70">
                <RefreshCcw className="h-4 w-4" />
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : activeRows.length === 0 ? (
              <div className="text-center py-12 text-white/40">
                <active.icon className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">{t('لا توجد بيانات مسجّلة بعد', 'No records yet')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {activeRows.map((r) => (
                  <AssetRow
                    key={r.id}
                    row={r}
                    tab={tab}
                    color={active.color}
                    onDelete={deleteRow}
                    t={t}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────
function TotalCard({ color, labelAr, labelEn, count, value, testId }) {
  return (
    <div
      className="rounded-xl border p-4 flex items-center justify-between"
      style={{ backgroundColor: `${color}0f`, borderColor: `${color}40` }}
      data-testid={testId}
    >
      <div>
        <p className="text-xs mb-1" style={{ color }}>
          <ArLabel ar={labelAr} en={labelEn} />
        </p>
        <p className="text-white font-bold text-lg">
          <DirhamPrice value={floorAED(value || 0)} decimals={2} />
        </p>
        <p className="text-[10px] text-white/40 mt-0.5">{count || 0} <ArLabel ar="عنصر" en="items" /></p>
      </div>
    </div>
  );
}

function ArLabel({ ar, en }) {
  const { language } = useLanguage();
  return language === 'ar' ? ar : en;
}

function LabeledInput({ label, value, onChange, type = 'text', placeholder, dir, testid }) {
  return (
    <div>
      <label className="block text-xs text-white/60 mb-1">{label}</label>
      <Input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        dir={dir}
        className="bg-[#0a0a0a] border-white/10 text-white h-10"
        data-testid={testid}
      />
    </div>
  );
}

function LabeledSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs text-white/60 mb-1">{label}</label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 bg-[#0a0a0a] border border-white/10 text-white rounded-md px-3 text-sm"
        data-testid={testid}
      >
        {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </div>
  );
}

function MetalsForm({ form, setForm, t }) {
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <LabeledSelect
        label={t('النوع', 'Type')}
        value={form.metal_type}
        onChange={set('metal_type')}
        options={[
          { value: 'gold',    label: t('ذهب',   'Gold') },
          { value: 'silver',  label: t('فضة',   'Silver') },
          { value: 'diamond', label: t('ماس',   'Diamond') },
          { value: 'other',   label: t('أخرى',  'Other') },
        ]}
        testid="asset-metal-type"
      />
      <LabeledInput label={t('المسمى/الوصف', 'Name / Description')} value={form.name} onChange={set('name')} testid="asset-metal-name" />
      <LabeledInput label={t('الوزن (جرام)', 'Weight (grams)')} type="number" value={form.weight_grams} onChange={set('weight_grams')} testid="asset-metal-weight" />
      <LabeledInput label={t('العيار', 'Karat')} value={form.karat} onChange={set('karat')} placeholder="24K / 22K / 18K" testid="asset-metal-karat" />
      <LabeledInput label={t('سعر الشراء (د.إ)', 'Purchase Price (AED)')} type="number" value={form.purchase_price_aed} onChange={set('purchase_price_aed')} testid="asset-metal-price" />
      <LabeledInput label={t('المبلغ المدفوع (د.إ)', 'Paid Amount (AED)')} type="number" value={form.paid_amount_aed} onChange={set('paid_amount_aed')} testid="asset-metal-paid" />
      <LabeledInput label={t('تاريخ الشراء', 'Purchase Date')} type="date" value={form.purchase_date} onChange={set('purchase_date')} testid="asset-metal-date" />
      <LabeledInput label={t('البائع', 'Seller')} value={form.seller} onChange={set('seller')} testid="asset-metal-seller" />
      <LabeledInput label={t('المكان', 'Location')} value={form.location} onChange={set('location')} testid="asset-metal-location" />
      <div className="md:col-span-3">
        <label className="block text-xs text-white/60 mb-1">{t('ملاحظات', 'Notes')}</label>
        <Textarea value={form.notes} onChange={(e) => set('notes')(e.target.value)} className="bg-[#0a0a0a] border-white/10 text-white" rows={2} data-testid="asset-metal-notes" />
      </div>
    </div>
  );
}

function StocksForm({ form, setForm, t }) {
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  // Auto-compute purchase_price = qty * unit_price
  useEffect(() => {
    const q = Number(form.quantity);
    const u = Number(form.unit_price_aed);
    if (!isNaN(q) && !isNaN(u) && q > 0 && u > 0) {
      setForm((f) => ({ ...f, purchase_price_aed: floorAED(q * u) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.quantity, form.unit_price_aed]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <LabeledInput label={t('رمز السهم (Ticker) *', 'Stock Symbol *')} value={form.stock_symbol} onChange={set('stock_symbol')} dir="ltr" placeholder="AAPL, TSLA…" testid="asset-stock-symbol" />
      <LabeledInput label={t('اسم السهم', 'Stock Name')} value={form.stock_name} onChange={set('stock_name')} testid="asset-stock-name" />
      <LabeledSelect
        label={t('النوع', 'Type')}
        value={form.stock_type}
        onChange={set('stock_type')}
        options={[
          { value: 'common',    label: t('عادي',    'Common') },
          { value: 'preferred', label: t('ممتاز',   'Preferred') },
          { value: 'etf',       label: 'ETF' },
          { value: 'other',     label: t('أخرى',    'Other') },
        ]}
        testid="asset-stock-type"
      />
      <LabeledInput label={t('السوق', 'Market')} value={form.market} onChange={set('market')} placeholder="US / DFM / ADX" testid="asset-stock-market" />
      <LabeledInput label={t('الكمية', 'Quantity')} type="number" value={form.quantity} onChange={set('quantity')} testid="asset-stock-qty" />
      <LabeledInput label={t('سعر السهم (د.إ)', 'Unit Price (AED)')} type="number" value={form.unit_price_aed} onChange={set('unit_price_aed')} testid="asset-stock-unit-price" />
      <LabeledInput label={t('سعر الشراء الإجمالي (د.إ)', 'Total Purchase Price (AED)')} type="number" value={form.purchase_price_aed} onChange={set('purchase_price_aed')} testid="asset-stock-total-price" />
      <LabeledInput label={t('المبلغ المدفوع (د.إ)', 'Paid Amount (AED)')} type="number" value={form.paid_amount_aed} onChange={set('paid_amount_aed')} testid="asset-stock-paid" />
      <LabeledInput label={t('تاريخ الشراء', 'Purchase Date')} type="date" value={form.purchase_date} onChange={set('purchase_date')} testid="asset-stock-date" />
      <LabeledInput label={t('الوسيط', 'Broker')} value={form.broker} onChange={set('broker')} testid="asset-stock-broker" />
      <div className="md:col-span-3">
        <label className="block text-xs text-white/60 mb-1">{t('ملاحظات', 'Notes')}</label>
        <Textarea value={form.notes} onChange={(e) => set('notes')(e.target.value)} className="bg-[#0a0a0a] border-white/10 text-white" rows={2} data-testid="asset-stock-notes" />
      </div>
    </div>
  );
}

function RealEstateForm({ form, setForm, t }) {
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <LabeledSelect
        label={t('نوع العقار', 'Property Type')}
        value={form.property_type}
        onChange={set('property_type')}
        options={[
          { value: 'apartment', label: t('شقة',     'Apartment') },
          { value: 'villa',     label: t('فيلا',    'Villa') },
          { value: 'building',  label: t('بناية',   'Building') },
          { value: 'land',      label: t('أرض',     'Land') },
          { value: 'office',    label: t('مكتب',    'Office') },
          { value: 'shop',      label: t('محل',     'Shop') },
          { value: 'warehouse', label: t('مستودع',  'Warehouse') },
        ]}
        testid="asset-re-type"
      />
      <LabeledInput label={t('المسمى', 'Name')} value={form.name} onChange={set('name')} testid="asset-re-name" />
      <LabeledInput label={t('الإمارة', 'Emirate')} value={form.emirate} onChange={set('emirate')} testid="asset-re-emirate" />
      <div className="md:col-span-3">
        <label className="block text-xs text-white/60 mb-1">{t('العنوان', 'Address')}</label>
        <Input value={form.address ?? ''} onChange={(e) => set('address')(e.target.value)} className="bg-[#0a0a0a] border-white/10 text-white h-10" data-testid="asset-re-address" />
      </div>
      <LabeledInput label={t('المساحة (م²)', 'Area (sqm)')} type="number" value={form.area_sqm} onChange={set('area_sqm')} testid="asset-re-area" />
      <LabeledInput label={t('غرف نوم', 'Bedrooms')} type="number" value={form.bedrooms} onChange={set('bedrooms')} testid="asset-re-bedrooms" />
      <LabeledInput label={t('حمامات', 'Bathrooms')} type="number" value={form.bathrooms} onChange={set('bathrooms')} testid="asset-re-bathrooms" />
      <LabeledInput label={t('سعر الشراء (د.إ)', 'Purchase Price (AED)')} type="number" value={form.purchase_price_aed} onChange={set('purchase_price_aed')} testid="asset-re-price" />
      <LabeledInput label={t('المبلغ المدفوع (د.إ)', 'Paid Amount (AED)')} type="number" value={form.paid_amount_aed} onChange={set('paid_amount_aed')} testid="asset-re-paid" />
      <LabeledInput label={t('تاريخ الشراء', 'Purchase Date')} type="date" value={form.purchase_date} onChange={set('purchase_date')} testid="asset-re-date" />
      <LabeledInput label={t('البائع', 'Seller')} value={form.seller} onChange={set('seller')} testid="asset-re-seller" />
      <LabeledInput label={t('رقم سند الملكية', 'Title Deed Number')} value={form.title_deed_number} onChange={set('title_deed_number')} testid="asset-re-deed" />
      <div className="md:col-span-3">
        <label className="block text-xs text-white/60 mb-1">{t('ملاحظات', 'Notes')}</label>
        <Textarea value={form.notes} onChange={(e) => set('notes')(e.target.value)} className="bg-[#0a0a0a] border-white/10 text-white" rows={2} data-testid="asset-re-notes" />
      </div>
    </div>
  );
}

function AssetRow({ row, tab, color, onDelete, t }) {
  const primaryTitle = row.name || row.stock_name || row.stock_symbol || t('بدون اسم', 'Unnamed');
  const subline = tab === 'metals'
    ? [row.metal_type, row.karat, row.weight_grams && `${row.weight_grams}g`].filter(Boolean).join(' · ')
    : tab === 'stocks'
      ? [row.stock_symbol, row.quantity && `qty=${row.quantity}`, row.market].filter(Boolean).join(' · ')
      : [row.property_type, row.emirate, row.area_sqm && `${row.area_sqm}m²`].filter(Boolean).join(' · ');
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-[#0a0a0a] border border-white/5 p-3 hover:border-white/20 transition-colors" data-testid={`asset-row-${row.id}`}>
      <div className="min-w-0">
        <p className="text-white font-semibold truncate">{primaryTitle}</p>
        <p className="text-xs text-white/40 mt-0.5">{subline}</p>
      </div>
      <div className="text-end shrink-0">
        <p className="text-white font-bold"><DirhamPrice value={floorAED(row.purchase_price_aed || 0)} decimals={2} /></p>
        <p className="text-[10px] text-white/40">{t('مدفوع', 'Paid')}: <DirhamPrice value={floorAED(row.paid_amount_aed || 0)} decimals={2} /></p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onDelete(row.id)}
        className="h-8 px-2 border-red-500/30 text-red-400 hover:bg-red-500/10"
        data-testid={`asset-delete-${row.id}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
